import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import ApiClient from '../../api/ReservationApi'
import { toAvailabilityRequestForPricing } from '../../api/data/AvailabilityRequest'
import type { AvailableItem, Rate } from '../../api/data/AvailableVehicle'
import { type LocationExtended } from '../../api/data/LocationExtended'
import type { ReservationRequest } from '../../api/data/ReservationRequest'
import type { ReservationResponse } from '../../api/data/ReservationResponse'
import { type RezResponse } from '../../api/data/RezResponse'
import { useGenerateAvailability, useGetLocationData } from '../../api/hooks'
import configs from '../../configs/configs'
import { dateFormat } from '../../helper/DateModel'
import IntlHelper from '../../helper/IntlHelper'
import type { ReviewAddonData } from '../../models/ReviewAddonData'
import type { ReviewRezInfo } from '../../models/ReviewRezInfo'
import { getConfig } from '../../services/config-service'
import { confirmReservationAction, setLocationData } from '../../store/reservationSlice'
import type { RootState } from '../../store/store'
import Modal from '../Modal/Modal'
import ReviewRezAddOn from '../elements/ReviewRezAddOn/ReviewRezAddOn'
import styles from './ReviewRezTab.module.scss'
import { Channel } from '../../configs/constant'
import type { PatronAutomobile } from '../../api/data/PatronAutomobile'

const ReviewRezTab = (): any => {
  let isDataLoaded: boolean = false
  const intlHelper = new IntlHelper()

  const reservation = useSelector((state: RootState) => state.reservationSlice.reservation)
  const dealerNumber = reservation?.dealerNumber ?? ''
  const oemName = reservation?.oemName ?? ''
  const locationExtended: undefined | LocationExtended = useSelector((state: RootState) => state.reservationSlice.locationData)
  const refreshLocation = typeof locationExtended === 'undefined'
  const { locationData } = useGetLocationData(reservation?.oemName, reservation?.dealerNumber, refreshLocation)
  const dealerLocData = locationExtended ?? locationData.data

  const [testDriveRequirements, setTestDriveRequirements] = useState('')

  const configValue = getConfig<string>('testDriveRequirements', false)

  const availableRatesToDisplay: ReviewAddonData[] = []

  useEffect(() => {
    if (locationData !== null && locationData?.statusCode === 200 && locationData?.data !== null) {
      dispatch(setLocationData(locationData.data))
    }
    setTestDriveRequirements(configValue?.replace(/^"|"$/g, '') ?? '')
  }, [locationData, configValue])

  const rezReviewVM: ReviewRezInfo = {
    imgPath: reservation?.modelGroupImage ?? '',
    modelGroupName: reservation?.modelGroup ?? 'Please select a vehicle',
    dealerAddress: 'Unavailable'
  }

  if (dealerLocData?.locationAddress?.city !== undefined) {
    let address2 = dealerLocData?.locationAddress?.address2 ?? ''
    address2 = address2 === '' ? address2 : ', ' + address2
    rezReviewVM.dealerAddress = (dealerLocData?.locationAddress?.address1 ?? '').concat(
      address2,
      ', ',
      dealerLocData?.locationAddress?.city ?? '',
      ', ',
      dealerLocData?.locationAddress?.region ?? '',
      ' ',
      dealerLocData?.locationAddress?.postalCode ?? ''
    )
  }
  const resAvailbilityReq = toAvailabilityRequestForPricing(reservation as ReservationRequest)
  const { vehicles } = useGenerateAvailability(resAvailbilityReq, true)

  const [currentVehicles, setCurrentVehicles] = useState(vehicles.data)
  useEffect(() => {
    if (vehicles.data !== null) {
      setCurrentVehicles(vehicles.data)
    }
  }, [vehicles])

  function getRates(): void {
    const availableVehicles: AvailableItem[] = currentVehicles?.availableItems as AvailableItem[]

    if (availableVehicles?.length > 0) {
      rezReviewVM.modelGroupName = availableVehicles[0].modelGroup?.name

      const availableRates: Rate[] = availableVehicles[0].rates as Rate[]

      if (availableRates.length > 0) {
        rezReviewVM.tax = availableRates[0].costSummary?.totalTaxes ?? 0
        rezReviewVM.subTotal = (availableRates[0].costSummary?.totalCharges ?? 0) - (availableRates[0].costSummary?.totalTaxes ?? 0)
        rezReviewVM.total = availableRates[0].costSummary?.totalCharges ?? 0

        if ((availableRates[0].costSummary?.hourlyFee ?? 0) > 0) {
          let frequencyDescriptionTemp = 'Hours'
          if ((availableRates[0].costSummary?.billingHours ?? 0) === 1) {
            frequencyDescriptionTemp = 'Hour'
          }
          const hourlyRateToDisplay: ReviewAddonData = {
            addOnName: 'Hourly Rate',
            addOnPrice: isNaN(availableRates[0].costSummary?.hourlyFee ?? Number('XYZ'))
              ? 'UnAvailable'
              : (availableRates[0].costSummary?.hourlyFee ?? 0).toString(),
            frequencyQuantity: isNaN(availableRates[0].costSummary?.billingHours ?? Number('XYZ'))
              ? 'UnAvailable'
              : (availableRates[0].costSummary?.billingHours ?? 0).toString(),
            frequencyDescription: frequencyDescriptionTemp,
            unitRate: isNaN(availableRates[0].hourlyRate ?? Number('XYZ')) ? 'UnAvailable' : (availableRates[0].hourlyRate ?? 0).toString(),
            displayFrequency: true
          }
          availableRatesToDisplay.push(hourlyRateToDisplay)
        }

        if ((availableRates[0].costSummary?.dailyFee ?? 0) > 0) {
          let frequencyDescriptionTemp = 'Days'
          if ((availableRates[0].costSummary?.billingDays ?? 0) === 1) {
            frequencyDescriptionTemp = 'Day'
          }
          const dailyRateToDisplay: ReviewAddonData = {
            addOnName: 'Daily Rate',
            addOnPrice: isNaN(availableRates[0].costSummary?.dailyFee ?? Number('XYZ'))
              ? 'UnAvailable'
              : (availableRates[0].costSummary?.dailyFee ?? 0).toString(),
            frequencyQuantity: isNaN(availableRates[0].costSummary?.billingDays ?? Number('XYZ'))
              ? 'UnAvailable'
              : (availableRates[0].costSummary?.billingDays ?? 0).toString(),
            frequencyDescription: frequencyDescriptionTemp,
            unitRate: isNaN(availableRates[0].dailyRate ?? Number('XYZ')) ? 'UnAvailable' : (availableRates[0].dailyRate ?? 0).toString(),
            displayFrequency: true
          }
          availableRatesToDisplay.push(dailyRateToDisplay)
        }

        if ((availableRates[0].costSummary?.weeklyFee ?? 0) > 0) {
          let frequencyDescriptionTemp = 'Weeks'
          if ((availableRates[0].costSummary?.billingWeeks ?? 0) === 1) {
            frequencyDescriptionTemp = 'Week'
          }
          const weeklyRateToDisplay: ReviewAddonData = {
            addOnName: 'Weekly Rate',
            addOnPrice: isNaN(availableRates[0].costSummary?.weeklyFee ?? Number('XYZ'))
              ? 'UnAvailable'
              : (availableRates[0].costSummary?.weeklyFee ?? 0).toString(),
            frequencyQuantity: isNaN(availableRates[0].costSummary?.billingWeeks ?? Number('XYZ'))
              ? 'UnAvailable'
              : (availableRates[0].costSummary?.billingWeeks ?? 0).toString(),
            frequencyDescription: frequencyDescriptionTemp,
            unitRate: isNaN(availableRates[0].weeklyRate ?? Number('XYZ')) ? 'UnAvailable' : (availableRates[0].weeklyRate ?? 0).toString(),
            displayFrequency: true
          }
          availableRatesToDisplay.push(weeklyRateToDisplay)
        }

        if ((availableRates[0].costSummary?.monthlyFee ?? 0) > 0) {
          let frequencyDescriptionTemp = 'Months'
          if ((availableRates[0].costSummary?.billingMonths ?? 0) === 1) {
            frequencyDescriptionTemp = 'Month'
          }
          const monthlyRateToDisplay: ReviewAddonData = {
            addOnName: 'Monthly Rate',
            addOnPrice: isNaN(availableRates[0].costSummary?.monthlyFee ?? Number('XYZ'))
              ? 'UnAvailable'
              : (availableRates[0].costSummary?.monthlyFee ?? 0).toString(),
            frequencyQuantity: isNaN(availableRates[0].costSummary?.billingMonths ?? Number('XYZ'))
              ? 'UnAvailable'
              : (availableRates[0].costSummary?.billingMonths ?? 0).toString(),
            frequencyDescription: frequencyDescriptionTemp,
            unitRate: isNaN(availableRates[0].monthlyRate ?? Number('XYZ')) ? 'UnAvailable' : (availableRates[0].monthlyRate ?? 0).toString(),
            displayFrequency: true
          }
          availableRatesToDisplay.push(monthlyRateToDisplay)
        }
      }
    }
  }

  getRates()

  const [showModal, setShowModal] = useState(false)

  isDataLoaded = true

  const dispatch = useDispatch()

  const {
    handleSubmit,
    formState: { isSubmitting }
  } = useForm<ReviewRezInfo>()

  const onSubmit = async (data: ReviewRezInfo): Promise<any> => {
    await handleNext(data)
  }

  async function handleNext(data: ReviewRezInfo): Promise<void> {
    const vehicle: PatronAutomobile = {
      autoYear: Number(reservation?.customer?.vehicleYear),
      vin: reservation?.customer?.VIN,
      autoMake: reservation?.customer?.vehicleMake,
      autoModel: reservation?.customer?.vehicleModel
    }

    const updtReservation = {
      ...reservation,
      customer: reservation?.customer,
      patronAutomobile: vehicle,
      channel: Channel
    }

    const resResponse = await createReservationOnServer(updtReservation)

    dispatch(confirmReservationAction(resResponse))
  }

  const [customError, setCustomError] = useState<string | null>(null)
  async function createReservationOnServer(updtReservation: ReservationRequest): Promise<RezResponse<ReservationResponse> | undefined> {
    try {
      const api = new ApiClient(configs.REZ_URL)
      const url = `/api/Reservation/Partner/CreateReservation?dealerNumber=${dealerNumber}&oemName=${oemName}`
      const resResponse = await api.post<ReservationRequest, RezResponse<ReservationResponse>>(url, updtReservation)
      return resResponse
    } catch (error) {
      setCustomError('An error occurred during Reservation creation.')
    }
  }

  if (!isDataLoaded) {
    return (
      <div className={styles.ReviewRezTab} data-testid='ReviewRezTab'>
        <p>Loading...</p>
      </div>
    )
  }

  return (
    <div className={styles.ReviewRezTab} data-testid='ReviewRezTab'>
      {customError != null || vehicles.errorMessage.length > 0 ? <p className='errorMessage'>{customError ?? vehicles.errorMessage}</p> : ''}
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <div className='grid-x grid-margin-x'>
          <div className='cell'>
            <div className={styles.CheckoutSummary}>
              <div className={styles.CheckoutSummaryItem}>
                <div className='grid-x grid-margin-x'>
                  <div className='cell small-12 medium-7'>
                    <img src={rezReviewVM.imgPath}></img>
                  </div>
                  <div className='cell small-12 medium-5 align-self-middle'>
                    <div className='item-name'>
                      <div className={styles.Vehicle}>{rezReviewVM.modelGroupName}</div>
                      <p>
                        <span className="material-symbols-outlined">arrow_left_alt</span> Checkout: <span style={{ fontWeight: '700' }}> {dateFormat(reservation?.pickupDateTime ?? new Date(), 'short_month_date')} at {dateFormat(reservation?.pickupDateTime ?? new Date(), 'time_only_with_am_pm')}  </span><br/>
                        <span className="material-symbols-outlined">arrow_right_alt</span> Return: <span style={{ fontWeight: '700' }}>{dateFormat(reservation?.dropOffDateTime ?? new Date(), 'short_month_date')} at&nbsp;
                        {dateFormat(reservation?.dropOffDateTime ?? new Date(), 'time_only_with_am_pm')}</span>
                      </p>
                      <p><span className="material-symbols-outlined">location_on</span> {rezReviewVM.dealerAddress}</p>
                    </div>
                  </div>
                </div>
              </div>
              {availableRatesToDisplay.map((item, index) => (
                <ReviewRezAddOn
                  key={index}
                  addOnName={item.addOnName}
                  addOnPrice={item.addOnPrice}
                  rateFee={item.unitRate}
                  rateFrequency={item.frequencyQuantity}
                  rateType={item.frequencyDescription}
                />
              ))}
              {
              !(rezReviewVM.tax === 0 && rezReviewVM.subTotal === 0 && rezReviewVM.total === 0)
                ? <div className={styles.CheckoutSummaryDetails}>
                <div className={styles.CheckoutSummaryDetailsLeft}>
                  <p>
                    <strong>Subtotal:</strong>
                  </p>
                  <p>
                    <strong>Tax:</strong>
                  </p>
                  <p>
                    <strong>Total:</strong>
                  </p>
                  <small>You will not be charged until you receive your vehicle.</small>
                </div>
                <div className={styles.CheckoutSummaryDetailsRight}>
                  <p>{intlHelper.CurrencyFormat(rezReviewVM.subTotal ?? 0)}</p>
                  <p>{intlHelper.CurrencyFormat(rezReviewVM.tax ?? 0)}</p>
                  <p>{intlHelper.CurrencyFormat(rezReviewVM.total ?? 0)}</p>
                </div>
              </div>
                : ''}
            </div>
            <div>
                <button disabled={isSubmitting} className='button primary expanded' data-testid='Button' type='submit'>
                  {'SUBMIT RESERVATION'}
                </button>
              </div>

              <div className={styles.CheckoutPolicies}>
              By continuing, I accept the
                <a
                  href='#'
                  onClick={() => {
                    setShowModal(true)
                  }}
                >
                  Terms and Conditions
                </a>
                {showModal ? <Modal headerTitleText='Terms and Conditions' htmlContentText={testDriveRequirements} toggleModal={setShowModal} /> : null}
              </div>
          </div>
        </div>
      </form>
    </div>
  )
}

export default ReviewRezTab
